import _ from 'lodash';
import { PropTypes } from 'prop-types';
import React, { useState, useMemo } from 'react';

import StudyContext from './study-context';
import ScreenRecorder from './screen-recorder';

import {
  CenteredMessage,
  Container,
  ContainerCentered,
  ErrorMessage,
  Loader,
  Message,
  ScreenContainer,
  ScreenRecorderContainer,
  TextCentered,
} from './styles';

const BasePreviewStudy = ({ questionSource, error, loading }) => {
  const [startRecording, setStartRecording] = useState(false);

  const handleStartRecording = () => {
    setStartRecording(true);
  };

  const hasContext = useMemo(() => questionSource
      && (questionSource.studyContext || questionSource.templateContext)
      && !startRecording, [questionSource, startRecording]);

  const survey = useMemo(() => {
    const { surveys } = questionSource;

    const sessionSurvey = _.chain(surveys)
      .filter({ surveyType: 'session' })
      .first()
      .value();

    return sessionSurvey ? {
      ...sessionSurvey,
      questions: _.sortBy(sessionSurvey.questions, (question) => question.questionOrder),
    } : null;
  }, [questionSource]);

  if (loading) {
    return (
      <ContainerCentered>
        <Loader data-loader="base-preview-study-loader" />
      </ContainerCentered>
    );
  }

  if (error) {
    return (
      <ContainerCentered>
        <ErrorMessage>
          Error while retrieving study preview.
        </ErrorMessage>
      </ContainerCentered>
    );
  }

  if (!survey) {
    return (
      <ContainerCentered>
        <ErrorMessage>
          This project has no study survey to preview.
        </ErrorMessage>
      </ContainerCentered>
    );
  }

  return (
    <Container>
      <Message>
        Feel free to share this preview with colleagues!
      </Message>

      <ScreenContainer>
        {hasContext && (
          <StudyContext
            studyContext={questionSource.studyContext || questionSource.templateContext}
            onStartRecording={handleStartRecording}
          />
        )}

        {(!hasContext || startRecording) && (
          <ScreenRecorderContainer>
            <ScreenRecorder survey={survey} />
          </ScreenRecorderContainer>
        )}

        <TextCentered hasContext={hasContext}>
          <CenteredMessage>
            Since ‘Preview Study’ is not a live test, the links you’ve entered won’t load here.
            However, when the study is launched and live, the links will automatically load for testers.
          </CenteredMessage>
        </TextCentered>
      </ScreenContainer>
    </Container>
  );
};

BasePreviewStudy.propTypes = {
  questionSource: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    testType: PropTypes.string,
    studyContext: PropTypes.string,
    templateContext: PropTypes.string,
    surveys: PropTypes.arrayOf({
      id: PropTypes.string.isRequired,
      surveyType: PropTypes.string.isRequired,
      questions: PropTypes.arrayOf({
        id: PropTypes.string.isRequired,
        label_preview: PropTypes.string,
        label: PropTypes.string.isRequired,
        questionType: PropTypes.string.isRequired,
        questionOrder: PropTypes.number.isRequired,
        leftLabel: PropTypes.string,
        right_label: PropTypes.string,
        questionOptions: PropTypes.arrayOf({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
        }),
      }).isRequired,
    }).isRequired,
  }).isRequired,
  error: PropTypes.object,
  loading: PropTypes.bool.isRequired,
};

BasePreviewStudy.defaultProps = {
  error: null,
};

export default BasePreviewStudy;
